import {
  BillingRegistryPlan,
  BillingRegistryPlanFrequency,
  BillingRegistryPlanPurchase,
  BillingRegistryPlanPurchaseState as State
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import type {
  BillingRegistryGetRoomBillingInfoResponse,
  BillingRegistryGetUsageResponse
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';

export type PurchaseStatus =
  | 'unconfirmed'
  | 'checking'
  | 'open'
  | 'pending'
  | 'payment-received'
  | 'scheduled'
  | 'activating'
  | 'payment-required'
  | 'failed'
  | 'canceling'
  | 'canceled'
  | 'confirmed'
  | 'ready'
  | 'expired'
  | 'unavailable';
export interface PlanPurchaseView {
  readonly roomId: string;
  readonly sessionId: string;
  readonly status: PurchaseStatus;
  readonly receipt?: BillingRegistryPlanPurchase | undefined;
  readonly receiptVerified?: boolean;
  readonly cancellationRequested?: boolean;
  readonly noticeShown?: boolean;
  readonly portalQuote?: { plan: BillingRegistryPlan; frequency: BillingRegistryPlanFrequency };
  readonly previousSessionId?: string | undefined;
  readonly observedPlan?: { plan: BillingRegistryPlan; frequency: BillingRegistryPlanFrequency };
}
interface Ports {
  vendor: 'iris' | 'ptzoptics';
  actorKey: string;
  isCurrent?: () => boolean;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  confirm: (roomId: string, sessionId: string) => Promise<BillingRegistryPlanPurchase>;
  cancel?: (roomId: string, sessionId: string) => Promise<BillingRegistryPlanPurchase>;
  canCancel?: (roomId: string) => boolean;
  load: (roomId: string) => Promise<{
    billing: BillingRegistryGetRoomBillingInfoResponse;
    usage: BillingRegistryGetUsageResponse;
  }>;
  refresh: (roomId: string) => Promise<void>;
  pollMilliseconds?: number;
  maxPolls?: number;
}
const validRoom = (id: string) => /^[a-f0-9]{32}$/i.test(id);
const emptyViews: Readonly<Record<string, PlanPurchaseView>> = {};
const cancellationSettled = (receipt: BillingRegistryPlanPurchase): boolean =>
  receipt.state === State.PURCHASE_STATE_CONFIRMED ||
  (!receipt.recoveryPending &&
    (receipt.state === State.PURCHASE_STATE_CANCELED ||
      receipt.state === State.PURCHASE_STATE_EXPIRED ||
      receipt.state === State.PURCHASE_STATE_FAILED));

// One server-owned identity is shared by status, cancellation and hosted-invoice recovery.
export const matchesPlanPurchase = (
  expected: BillingRegistryPlanPurchase,
  actual: BillingRegistryPlanPurchase
): boolean =>
  actual.checkoutSessionId === expected.checkoutSessionId &&
  actual.priceId === expected.priceId &&
  actual.plan === expected.plan &&
  actual.frequency === expected.frequency &&
  (expected.effectiveAt <= 0 ||
    (actual.quoteId === expected.quoteId &&
      actual.effectiveAt === expected.effectiveAt &&
      (expected.subscriptionId === '' || actual.subscriptionId === expected.subscriptionId)));

// A retained future receipt is positive evidence of an unresolved purchase, unlike a naked return hint.
export const hasUnresolvedFuturePurchase = (view: PlanPurchaseView | undefined): boolean =>
  view !== undefined &&
  (view.receipt?.effectiveAt ?? 0) > 0 &&
  view.status !== 'ready' &&
  (view.cancellationRequested === true ||
    view.receipt?.recoveryPending === true ||
    (view.receipt?.state !== State.PURCHASE_STATE_CANCELED &&
      view.receipt?.state !== State.PURCHASE_STATE_EXPIRED &&
      view.receipt?.state !== State.PURCHASE_STATE_FAILED));
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';
const isPlanQuote = (value: unknown): value is NonNullable<PlanPurchaseView['portalQuote']> =>
  isRecord(value) &&
  (value.plan === BillingRegistryPlan.PLAN_FREE ||
    value.plan === BillingRegistryPlan.PLAN_STANDARD ||
    value.plan === BillingRegistryPlan.PLAN_PRO) &&
  (value.frequency === BillingRegistryPlanFrequency.PLAN_FREQUENCY_MONTHLY ||
    value.frequency === BillingRegistryPlanFrequency.PLAN_FREQUENCY_YEARLY);

// Persist payment identity by product, authenticated account and Studio, never tokens or hosted URLs.
// This store owns return observation; it cannot initiate a purchase. Network
// adapters and active-Studio enforcement remain at their existing boundaries.
// Per regression-tests-for-state-machine-primitives (ai.knowledge/global).
export class PlanPurchaseCoordinator {
  private views: Readonly<Record<string, PlanPurchaseView>> = {};
  private readonly listeners = new Set<() => void>();
  private readonly inflight = new Map<
    string,
    {
      sessionId: string;
      portalQuote: PlanPurchaseView['portalQuote'];
      revision: number;
      operation: Promise<void>;
    }
  >();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly revisions = new Map<string, number>();
  private readonly storageKey: string;
  readonly actorKey: string;
  private disposed = false;

  constructor(private readonly ports: Ports) {
    if (typeof ports.actorKey !== 'string' || ports.actorKey === '')
      throw new Error('purchase account identity is unresolved');
    this.actorKey = ports.actorKey;
    // Legacy v1 records have no account owner; fresh authenticated Billing recovers them instead.
    this.storageKey = `iris.plan-purchases.v2.${ports.vendor}.${encodeURIComponent(ports.actorKey)}`;
    try {
      const saved: unknown = JSON.parse(ports.storage.getItem(this.storageKey) ?? '[]');
      if (Array.isArray(saved)) {
        for (const entry of saved) {
          const item: unknown = entry;
          if (
            !isRecord(item) ||
            typeof item.roomId !== 'string' ||
            !validRoom(item.roomId) ||
            typeof item.sessionId !== 'string'
          )
            continue;
          const receipt =
            item.receipt === undefined
              ? undefined
              : {
                  ...BillingRegistryPlanPurchase.fromJSON(item.receipt),
                  // Hosted actions require fresh owner authority, including older saved receipts.
                  checkoutUrl: '',
                  paymentUrl: '',
                  canCancel: false
                };
          if (receipt !== undefined && receipt.checkoutSessionId !== item.sessionId) continue;
          const portalQuote = isPlanQuote(item.portalQuote) ? item.portalQuote : undefined;
          const cancellationRequested =
            item.cancellationRequested === true &&
            (receipt === undefined || !cancellationSettled(receipt));
          const status: PurchaseStatus = cancellationRequested
            ? 'canceling'
            : receipt !== undefined
              ? this.receiptStatus(receipt)
              : item.sessionId !== '' || portalQuote !== undefined
                ? 'pending'
                : 'unconfirmed';
          this.views = {
            ...this.views,
            [item.roomId]: {
              roomId: item.roomId,
              sessionId: item.sessionId,
              status,
              receipt,
              cancellationRequested,
              ...(portalQuote === undefined ? {} : { portalQuote }),
              previousSessionId:
                typeof item.previousSessionId === 'string' &&
                item.previousSessionId.startsWith('cs_')
                  ? item.previousSessionId
                  : undefined,
              noticeShown: item.noticeShown === true
            }
          };
        }
      }
    } catch {
      // Unavailable or malformed local storage never establishes payment.
    }
  }

  private isCurrent = (): boolean => !this.disposed && (this.ports.isCurrent?.() ?? true);
  snapshot = (): Readonly<Record<string, PlanPurchaseView>> =>
    this.isCurrent() ? this.views : emptyViews;
  subscribe = (listener: () => void): (() => void) => {
    if (this.disposed) return () => {};
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private put(view: PlanPurchaseView): void {
    if (!this.isCurrent()) return;
    const previous = this.views[view.roomId];
    // A billing read can identify the historical Checkout while a Portal
    // observation is already running. Its completion retains that identity.
    if (view.portalQuote !== undefined && previous?.portalQuote === view.portalQuote) {
      view = { ...view, previousSessionId: view.previousSessionId ?? previous.previousSessionId };
    }
    this.views = { ...this.views, [view.roomId]: view };
    try {
      this.ports.storage.setItem(
        this.storageKey,
        JSON.stringify(
          Object.values(this.views).map((item) => ({
            roomId: item.roomId,
            sessionId: item.sessionId,
            noticeShown: item.noticeShown,
            cancellationRequested: item.cancellationRequested,
            portalQuote: item.portalQuote,
            previousSessionId: item.previousSessionId,
            receipt:
              item.receipt === undefined
                ? undefined
                : BillingRegistryPlanPurchase.toJSON({
                    ...item.receipt,
                    checkoutUrl: '',
                    paymentUrl: '',
                    canCancel: false
                  })
          }))
        )
      );
    } catch {
      // Keep the in-memory receipt usable if browser storage is unavailable.
    }
    for (const listener of this.listeners) listener();
  }

  returned(roomId: string, sessionId = ''): void {
    if (!this.isCurrent()) return;
    if (!validRoom(roomId) || (sessionId !== '' && !sessionId.startsWith('cs_'))) return;
    const current = this.views[roomId];
    if (current !== undefined) {
      if (sessionId === '' || current.sessionId === sessionId) return;
      // An old hosted return cannot replace a newer ordinary paid plan change.
      // Other identities are verified by the server, even when saved OPEN
      // state became stale while this browser or desktop app was closed.
      if (current.portalQuote !== undefined && current.previousSessionId === sessionId) return;
    }
    this.supersede(roomId);
    this.put({ roomId, sessionId, status: sessionId !== '' ? 'pending' : 'unconfirmed' });
  }

  register(
    roomId: string,
    receipt: BillingRegistryPlanPurchase,
    source: 'purchase' | 'billing' = 'purchase'
  ): void {
    if (!this.isCurrent()) return;
    if (!validRoom(roomId) || !receipt.checkoutSessionId.startsWith('cs_')) return;
    const current = this.views[roomId];
    if (source === 'billing' && current?.portalQuote !== undefined) {
      // Billing retains the original purchase after an ordinary Portal change.
      // That receipt identifies old returns, never the newly requested target.
      if (current.previousSessionId === undefined)
        this.put({ ...current, previousSessionId: receipt.checkoutSessionId });
      return;
    }
    if (
      current?.sessionId === receipt.checkoutSessionId &&
      (current.status === 'ready' ||
        (current.receiptVerified === true &&
          current.receipt?.state === State.PURCHASE_STATE_CONFIRMED &&
          receipt.state !== State.PURCHASE_STATE_CONFIRMED))
    )
      return;
    // Only a fresh terminal receipt fences delayed observations; restored data must be rechecked.
    if (
      current?.sessionId === receipt.checkoutSessionId &&
      current.receiptVerified &&
      current.receipt?.state === State.PURCHASE_STATE_CANCELED &&
      !current.receipt.recoveryPending &&
      receipt.state !== State.PURCHASE_STATE_CANCELED &&
      receipt.state !== State.PURCHASE_STATE_CONFIRMED
    )
      return;
    const cancellationRequested =
      current?.sessionId === receipt.checkoutSessionId &&
      current.cancellationRequested === true &&
      this.ports.canCancel?.(roomId) !== false &&
      !cancellationSettled(receipt);
    if (
      current?.sessionId !== receipt.checkoutSessionId ||
      source === 'purchase' ||
      // A stripped Billing read needs fresh owner authority after a finishing confirmation.
      (source === 'billing' && current?.receipt?.canCancel === true && !receipt.canCancel) ||
      (cancellationSettled(receipt) && current.receipt?.state !== receipt.state)
    )
      this.supersede(roomId);
    this.put({
      roomId,
      sessionId: receipt.checkoutSessionId,
      receipt,
      receiptVerified: true,
      cancellationRequested,
      status: cancellationRequested ? 'canceling' : this.receiptStatus(receipt),
      // A server receipt can be historical on a new browser or computer.
      // Only a purchase this client started or returned from gets a notice.
      noticeShown:
        current?.sessionId === receipt.checkoutSessionId
          ? current.noticeShown === true
          : source === 'billing' && receipt.state === State.PURCHASE_STATE_CONFIRMED
    });
  }

  consumeReady(roomId: string): boolean {
    if (!this.isCurrent()) return false;
    const view = this.views[roomId];
    if (view?.status !== 'ready' || view.noticeShown === true) return false;
    this.put({ ...view, noticeShown: true });
    return true;
  }

  registerPortal(
    roomId: string,
    plan: BillingRegistryPlan,
    frequency: BillingRegistryPlanFrequency
  ): void {
    if (!this.isCurrent()) return;
    if (!validRoom(roomId)) return;
    const current = this.views[roomId];
    this.supersede(roomId);
    this.put({
      roomId,
      sessionId: '',
      status: 'pending',
      portalQuote: { plan, frequency },
      previousSessionId: current?.sessionId !== '' ? current?.sessionId : current?.previousSessionId
    });
  }

  requestCancellation(roomId: string, sessionId: string): Promise<void> {
    const current = this.views[roomId];
    if (
      !this.isCurrent() ||
      this.ports.canCancel?.(roomId) === false ||
      current === undefined ||
      current.sessionId !== sessionId ||
      (current.receipt?.effectiveAt ?? 0) <= 0 ||
      (current.receipt !== undefined && cancellationSettled(current.receipt))
    )
      return Promise.resolve();
    if (!current.cancellationRequested) {
      // Persist the user's intent before waiting; a slow prior read cannot hold cancellation hostage.
      this.supersede(roomId, true);
      this.put({ ...current, cancellationRequested: true, status: 'canceling' });
    }
    return this.resume(roomId);
  }

  private supersede(roomId: string, interrupt = false): void {
    this.revisions.set(roomId, (this.revisions.get(roomId) ?? 0) + 1);
    this.clearTimer(roomId);
    if (interrupt) this.inflight.delete(roomId);
  }

  resume(roomId: string): Promise<void> {
    if (!this.isCurrent()) return Promise.resolve();
    // Billing refresh runs periodically and on focus. Once this client has
    // restored access, those reads need no lease-holding payment mutation.
    if (
      this.views[roomId]?.status === 'ready' ||
      this.views[roomId]?.status === 'expired' ||
      this.views[roomId]?.status === 'canceled' ||
      this.views[roomId]?.status === 'failed'
    ) {
      this.clearTimer(roomId);
      return Promise.resolve();
    }
    return this.run(roomId, 0);
  }

  resumePending(): void {
    if (!this.isCurrent()) return;
    for (const view of Object.values(this.views)) {
      if (view.status !== 'ready' && view.status !== 'expired' && view.status !== 'unconfirmed')
        void this.resume(view.roomId);
    }
  }

  private run(roomId: string, poll: number): Promise<void> {
    if (!this.isCurrent()) return Promise.resolve();
    const pending = this.inflight.get(roomId);
    const view = this.views[roomId];
    if (pending !== undefined) {
      if (
        pending.sessionId === view?.sessionId &&
        pending.portalQuote === view?.portalQuote &&
        pending.revision === (this.revisions.get(roomId) ?? 0)
      )
        return pending.operation;
      // The earlier response is ignored by check's identity comparison. Queue
      // the new return behind it instead of losing the only return notification.
      return pending.operation.then(() => this.resume(roomId));
    }
    // Joining in-flight work must retain its already-scheduled recovery check.
    this.clearTimer(roomId);
    const operation = this.check(roomId, poll).finally(() => {
      if (this.inflight.get(roomId)?.operation === operation) this.inflight.delete(roomId);
    });
    this.inflight.set(roomId, {
      sessionId: view?.sessionId ?? '',
      portalQuote: view?.portalQuote,
      revision: this.revisions.get(roomId) ?? 0,
      operation
    });
    return operation;
  }

  private receiptStatus(receipt: BillingRegistryPlanPurchase): PurchaseStatus {
    if (receipt.recoveryPending && receipt.state === State.PURCHASE_STATE_CANCELED)
      return 'canceling';
    if (
      receipt.recoveryPending &&
      (receipt.state === State.PURCHASE_STATE_FAILED ||
        receipt.state === State.PURCHASE_STATE_EXPIRED)
    )
      return 'unavailable';
    switch (receipt.state) {
      case State.PURCHASE_STATE_CONFIRMED:
        return 'confirmed';
      case State.PURCHASE_STATE_OPEN:
        return 'open';
      case State.PURCHASE_STATE_EXPIRED:
        return 'expired';
      case State.PURCHASE_STATE_PAYMENT_RECEIVED:
        return 'payment-received';
      case State.PURCHASE_STATE_SCHEDULED:
        return 'scheduled';
      case State.PURCHASE_STATE_ACTIVATING:
        return 'activating';
      case State.PURCHASE_STATE_PAYMENT_REQUIRED:
        return 'payment-required';
      case State.PURCHASE_STATE_FAILED:
        return 'failed';
      case State.PURCHASE_STATE_CANCELING:
        return 'canceling';
      case State.PURCHASE_STATE_CANCELED:
        return 'canceled';
      default:
        return 'pending';
    }
  }

  private async check(roomId: string, poll: number): Promise<void> {
    const initial = this.views[roomId];
    if (initial === undefined || (initial.sessionId === '' && initial.portalQuote === undefined))
      return;
    const revision = this.revisions.get(roomId) ?? 0;
    const isCurrent = () =>
      this.isCurrent() &&
      revision === (this.revisions.get(roomId) ?? 0) &&
      this.views[roomId]?.sessionId === initial.sessionId &&
      this.views[roomId]?.portalQuote === initial.portalQuote;
    this.put({
      ...initial,
      status: initial.cancellationRequested
        ? 'canceling'
        : initial.receipt?.state === State.PURCHASE_STATE_CONFIRMED
          ? 'confirmed'
          : 'checking'
    });
    let receipt = initial.receipt;
    let cancellationRequested = initial.cancellationRequested === true;
    let receiptVerified = initial.receiptVerified === true;
    try {
      if (initial.sessionId !== '') {
        const cancel = cancellationRequested && this.ports.canCancel?.(roomId) !== false;
        if (cancel && this.ports.cancel === undefined)
          throw new Error('purchase cancellation unavailable');
        const result = await (cancel ? this.ports.cancel! : this.ports.confirm)(
          roomId,
          initial.sessionId
        );
        if (!isCurrent()) return;
        if (
          result.checkoutSessionId !== initial.sessionId ||
          result.state === State.PURCHASE_STATE_NONE ||
          (initial.receipt !== undefined && !matchesPlanPurchase(initial.receipt, result))
        )
          throw new Error('purchase receipt identity mismatch');
        receipt = result;
        receiptVerified = true;
        cancellationRequested =
          cancellationRequested &&
          this.ports.canCancel?.(roomId) !== false &&
          !cancellationSettled(receipt);
        this.put({
          ...initial,
          receipt,
          receiptVerified,
          cancellationRequested,
          status: cancellationRequested ? 'canceling' : this.receiptStatus(receipt)
        });
        if (
          !cancellationRequested &&
          (receipt.state === State.PURCHASE_STATE_OPEN ||
            (receipt.state === State.PURCHASE_STATE_EXPIRED && !receipt.recoveryPending) ||
            // Future consent is not collected payment or a paid-access refresh.
            receipt.state === State.PURCHASE_STATE_SCHEDULED ||
            receipt.state === State.PURCHASE_STATE_PAYMENT_REQUIRED ||
            (receipt.state === State.PURCHASE_STATE_FAILED && !receipt.recoveryPending) ||
            (receipt.state === State.PURCHASE_STATE_CANCELED && !receipt.recoveryPending))
        )
          return;
      }
      if (!cancellationRequested) {
        const response = await this.ports.load(roomId);
        if (!isCurrent()) return;
        const expected =
          receipt?.state === State.PURCHASE_STATE_CONFIRMED ? receipt : initial.portalQuote;
        if (expected !== undefined) {
          await this.ports.refresh(roomId);
          if (!isCurrent()) return;
          const attached = response.billing.purchase;
          // A later ordinary Portal change can alter the paid subscription's SKU.
          // The same authoritative attached purchase proves continuity without
          // reapplying its original quote to today's paid plan.
          const samePaidSubscription =
            receipt?.state === State.PURCHASE_STATE_CONFIRMED &&
            receipt.subscriptionId !== '' &&
            attached?.state === State.PURCHASE_STATE_CONFIRMED &&
            attached.checkoutSessionId === receipt.checkoutSessionId &&
            attached.subscriptionId === receipt.subscriptionId &&
            attached.priceId === receipt.priceId &&
            attached.plan === receipt.plan &&
            attached.frequency === receipt.frequency &&
            (response.billing.plan === BillingRegistryPlan.PLAN_STANDARD ||
              response.billing.plan === BillingRegistryPlan.PLAN_PRO);
          const ready =
            response.billing.error === undefined &&
            response.usage.error === undefined &&
            response.usage.billingStateKnown === true &&
            response.usage.limits !== undefined &&
            response.billing.trialEnd === 0 &&
            response.usage.trialEnd === 0 &&
            ((response.billing.plan === expected.plan &&
              response.billing.frequency === expected.frequency) ||
              samePaidSubscription);
          if (ready) {
            this.put({
              ...initial,
              receipt,
              receiptVerified,
              cancellationRequested,
              status: 'ready',
              observedPlan: { plan: response.billing.plan, frequency: response.billing.frequency }
            });
            return;
          }
        }
        if (receipt?.state === State.PURCHASE_STATE_CONFIRMED)
          this.put({
            ...initial,
            receipt,
            receiptVerified,
            cancellationRequested,
            status: 'confirmed'
          });
      }
    } catch {
      if (!isCurrent()) return;
      this.put({
        ...initial,
        receipt,
        receiptVerified,
        cancellationRequested,
        status: cancellationRequested
          ? 'canceling'
          : receipt?.state === State.PURCHASE_STATE_CONFIRMED
            ? 'confirmed'
            : 'unavailable'
      });
      // Current limits retain their established permissive failure behavior.
      if (receipt?.state === State.PURCHASE_STATE_CONFIRMED) {
        try {
          await this.ports.refresh(roomId);
        } catch {
          /* The verified receipt survives failed access refresh. */
        }
      }
      // The webhook and return can contend for the same billing lease, and
      // reads can fail temporarily. Keep bounded checks on this same attempt;
      // neither a failed read nor another navigation authorizes a purchase.
    }
    if (isCurrent() && poll + 1 < (this.ports.maxPolls ?? 8)) {
      const timer = setTimeout(() => {
        this.timers.delete(roomId);
        if (isCurrent()) void this.run(roomId, poll + 1);
      }, this.ports.pollMilliseconds ?? 1500);
      this.timers.set(roomId, timer);
    }
  }

  private clearTimer(roomId: string): void {
    const timer = this.timers.get(roomId);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(roomId);
  }

  dispose(): void {
    // A detached account keeps its durable receipt, but late requests cannot revive its screen.
    this.disposed = true;
    for (const room of this.timers.keys()) this.clearTimer(room);
    this.inflight.clear();
    this.revisions.clear();
    this.views = {};
    this.listeners.clear();
  }
}
